import { useLocation, useNavigate } from "react-router";
import * as ROUTES from "../../constants/routes";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";

import { TradeTermsLayout } from "../../components/tradeTermsAssignment/TradeTermsLayout";
import { useCustomerZone } from "../../hooks/api/v2/customerZones/useCustomerZone";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { Button, Icon, InputOption, InputSelect } from "@sellar-io/components";
import { CustomerZoneGetResponseType } from "@sellar-io/api-v2/types";
import { CustomerZoneDetails } from "./pages/CustomerZoneDetails";
import { CustomerZonePriceTier } from "./pages/CustomerZonePriceTier";
import { CustomerZoneProductCatalogue } from "./pages/CustomerZoneProductCatalogue";
import { SpinnerLegacy } from "../../components";
import { ReactNode, useEffect, useEffectEvent, useState } from "react";
import { useIsDesktop } from "../stockList/checkoutV2/lib/useIsDesktop";
import { twMerge } from "tailwind-merge";
import {
  Dialog,
  DialogNavigation,
  DialogCTA,
} from "../../components/modal/Dialog";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { CustomerZoneOrderRules } from "./pages/CustomerZoneOrderRules";
import { StorefrontPreviewer } from "../../components/storefrontPreviewer/StorefrontPreviewer";
import {
  StorefrontPreviewerTabs,
  useStorefrontPreviewerParameters,
} from "../../components/storefrontPreviewer/StorefrontPreviewer.shared";
import { useFeatureFlagStore } from "../../hooks/useFeatureFlags";
import { capitalize } from "@sellar-io/common";
import { CustomerZoneDeliveryMethods } from "./pages/CustomerZoneDeliveryMethods";
import { ListItemIconTile } from "@sellar-io/components";

type TabsType =
  | "details"
  | "priceList"
  | "productCatalogue"
  | "deliveryMethod"
  | "orderRules";

enum CustomerZonePages {
  DETAILS,
  PRICE_LIST,
  PRODUCT_CATALOGUE,
  DELIVERY_METHOD,
  ORDER_RULES,
}

const tabs: TabsType[] = [
  "details",
  "priceList",
  "productCatalogue",
  "deliveryMethod",
  "orderRules",
];

const getCurrentPage = (tab: TabsType | undefined): CustomerZonePages => {
  switch (tab) {
    default:
    case "details":
      return CustomerZonePages.DETAILS;
    case "priceList":
      return CustomerZonePages.PRICE_LIST;
    case "productCatalogue":
      return CustomerZonePages.PRODUCT_CATALOGUE;
    case "deliveryMethod":
      return CustomerZonePages.DELIVERY_METHOD;
    case "orderRules":
      return CustomerZonePages.ORDER_RULES;
  }
};

const getPageName = (tab: CustomerZonePages): TabsType => {
  switch (tab) {
    default:
    case CustomerZonePages.DETAILS:
      return "details";
    case CustomerZonePages.PRICE_LIST:
      return "priceList";
    case CustomerZonePages.PRODUCT_CATALOGUE:
      return "productCatalogue";
    case CustomerZonePages.DELIVERY_METHOD:
      return "deliveryMethod";
    case CustomerZonePages.ORDER_RULES:
      return "orderRules";
  }
};

export const CustomerZone = () => {
  const isSupplierStorefrontFeatureFlagEnabled = useFeatureFlagStore().isActive(
    "SUPPLIER_STOREFRONT_PREVIEW",
  );
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") ?? "details";
  const customerZoneId = useParamAsNumber(
    ROUTES.CUSTOMER_ZONE,
    "customerZoneId",
  );
  const [closeModalActive, setCloseModalActive] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(
    getCurrentPage(initialTab as TabsType),
  );

  const handleClose = () => {
    if (customerZone?.PriceTierId && customerZone.AvailabilityTierId) {
      navigate(ROUTES.CUSTOMER_ZONES);
    }
    setCloseModalActive(true);
  };

  const syncSelectedTab = useEffectEvent(() => {
    const newQueryParams = new URLSearchParams(location.search);
    newQueryParams.set("tab", getPageName(selectedIndex));
    navigate({ search: newQueryParams.toString() }, { replace: true });
  });

  useEffect(() => {
    syncSelectedTab();
  }, [selectedIndex]);

  const { data: customerZone, isLoading } = useCustomerZone(customerZoneId);

  if (
    customerZone?.systemDefinedType === "sellarDistribution" &&
    selectedIndex === CustomerZonePages.ORDER_RULES
  ) {
    setSelectedIndex(CustomerZonePages.DETAILS);
  }

  if (!customerZone || isLoading) return <SpinnerLegacy />;

  return (
    <TabGroup
      selectedIndex={selectedIndex}
      onChange={setSelectedIndex}
      className="h-full"
    >
      <TradeTermsLayout
        title={customerZone.name ?? ""}
        onClickLeftIcon={handleClose}
        icon="CHEVRON_LEFT"
        detailTitle="Configure"
        aside={
          <div className="flex flex-1 flex-col justify-between">
            <Tabs
              customerZone={customerZone}
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
            />
            {isSupplierStorefrontFeatureFlagEnabled ? (
              <StorefrontPreviewerButton customerZone={customerZone} />
            ) : null}
          </div>
        }
        assignmentHeader={<></>}
        hideFooter={true}
      >
        <div className="mx-auto flex w-full max-w-[920px] items-center gap-2">
          <div className="mb-8 flex w-full flex-col gap-4 px-4">
            <TabPanels>
              <TabPanel>
                <CustomerZoneDetails customerZone={customerZone} />
              </TabPanel>
              <TabPanel>
                <CustomerZonePriceTier
                  customerZoneName={customerZone.name}
                  customerZoneId={customerZone.id}
                  currentPriceTierId={customerZone.PriceTierId}
                />
              </TabPanel>
              <TabPanel>
                <CustomerZoneProductCatalogue
                  customerZoneId={customerZone.id}
                  currentAvailabilityTierId={customerZone.AvailabilityTierId}
                />
              </TabPanel>
              <TabPanel>
                <CustomerZoneDeliveryMethods
                  customerZoneId={customerZone.id}
                  customerZoneType={customerZone.type}
                  currentDrayDeliveryMethodId={
                    customerZone.DrayDeliveryMethodId
                  }
                  currentCourierDeliveryMethodId={
                    customerZone.CourierDeliveryMethodId
                  }
                  currentPalletDeliveryMethodId={
                    customerZone.PalletDeliveryMethodId
                  }
                />
              </TabPanel>
              <TabPanel>
                {customerZone.systemDefinedType !== "sellarDistribution" && (
                  <CustomerZoneOrderRules
                    customerZoneId={customerZone.id}
                    currentOrderRulesId={customerZone.OrderRulesId}
                  />
                )}
              </TabPanel>
            </TabPanels>
          </div>
        </div>
      </TradeTermsLayout>
      <CloseModal
        active={closeModalActive}
        onDismiss={() => {
          setCloseModalActive(false);
        }}
        customerZone={customerZone}
      />
    </TabGroup>
  );
};

const CloseModal = ({
  active,
  onDismiss,
  customerZone,
}: {
  active: boolean;
  onDismiss: () => void;
  customerZone: CustomerZoneGetResponseType;
}) => {
  const missingAssignments = [
    ...(!customerZone.AvailabilityTierId ? ["product catalogue"] : []),
    ...(!customerZone.PriceTierId ? ["price tier"] : []),
  ];

  return (
    <Dialog active={active}>
      <DialogNavigation
        title={`Assign ${missingAssignments.join(" and ")}`}
        description={`Your customer zone must have both a price tier and a product catalogue selected so that your customers see the correct pricing and products on your storefront`}
      />
      <DialogCTA>
        <Button onClick={onDismiss}>I understand</Button>
      </DialogCTA>
    </Dialog>
  );
};

const Tabs = ({
  customerZone,
  selectedIndex,
  setSelectedIndex,
}: {
  customerZone?: CustomerZoneGetResponseType;
  selectedIndex: number;
  setSelectedIndex: (value: number) => void;
}) => {
  const { isDesktop } = useIsDesktop();

  const unbundlingRelease2aEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2A",
  );

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const getTitle = (tab: TabsType) => {
    switch (tab) {
      case "details":
        return "Details";
      case "priceList":
        return "Pricing";
      case "productCatalogue":
        return "Product catalogue";
      case "deliveryMethod":
        return "Delivery method";
      case "orderRules":
        return "Order rules";
      default:
        return null;
    }
  };

  const getSubtitle = (tab: TabsType): ReactNode => {
    switch (tab) {
      case "details":
        return customerZone?.name;
      case "priceList":
        return customerZone?.PriceTier?.name;
      case "productCatalogue":
        return customerZone?.AvailabilityTier?.name;
      case "deliveryMethod": {
        const {
          DrayDeliveryMethod: dray,
          CourierDeliveryMethod: courier,
          PalletDeliveryMethod: pallet,
        } = customerZone || {};

        if (!dray && !courier && !pallet) return null;

        const format = (m: { name?: string | null; fulfillmentType: string }) =>
          m.name ?? capitalize(m.fulfillmentType);

        return (
          <span className="flex flex-col justify-start text-start">
            {/* Dray */}
            {customerZone?.type === "direct" && (
              <span>
                {dray ? (
                  format(dray)
                ) : (
                  <span className="text-blue-500">No dray method set!</span>
                )}
              </span>
            )}

            {/* Courier */}
            <span>
              {courier ? (
                format(courier)
              ) : (
                <span className="text-blue-500">No courier method set!</span>
              )}
            </span>

            {/* Pallet */}
            <span>
              {pallet ? (
                format(pallet)
              ) : (
                <span className="text-blue-500">No pallet method set!</span>
              )}
            </span>
          </span>
        );
      }
      case "orderRules":
        return customerZone?.OrderRules?.name;
      default:
        return null;
    }
  };

  if (isDesktop)
    return (
      <TabList className="flex flex-col">
        {tabs
          .filter((tab) => unbundlingRelease2aEnabled || tab !== "orderRules")
          .filter(
            (tab) =>
              customerZone?.systemDefinedType !== "sellarDistribution" ||
              tab !== "orderRules",
          )
          .filter(
            (tab) =>
              (unbundlingEnabled && tab !== "orderRules") || !unbundlingEnabled,
          )
          .map((tab, idx) => {
            if (
              (!unbundlingEnabled ||
                (unbundlingEnabled &&
                  customerZone?.systemDefinedType === "sellarDistribution")) &&
              tab === "deliveryMethod"
            )
              return <Tab key={idx}></Tab>; //this is a fudge to ensure the existing tabs work when different flags are on.
            const title = getTitle(tab);
            const subtitle = getSubtitle(tab);

            return (
              <Tab
                key={idx}
                className={({ selected }) =>
                  twMerge("focus:outline-none", selected && "bg-zinc-100")
                }
              >
                {
                  <div className="flex cursor-pointer items-center justify-between border-b border-zinc-300 p-4 hover:bg-zinc-50">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">{title}</span>
                      <span
                        className={twMerge(
                          "text-sm",
                          !subtitle ? "text-blue-500" : "text-zinc-700",
                        )}
                      >
                        {subtitle ?? `No ${title?.toLowerCase()} set!`}
                      </span>
                    </div>
                    <Icon icon="CHEVRON_RIGHT" />
                  </div>
                }
              </Tab>
            );
          })}
      </TabList>
    );

  return (
    <TabList>
      <InputSelect
        name="tabs"
        value={selectedIndex}
        onChange={(e) => setSelectedIndex(parseInt(e.target.value, 10))}
        className="bg-transparent py-2 font-semibold"
      >
        <Tab className="bg-transparent text-center">
          <InputOption value={CustomerZonePages.DETAILS}>Details</InputOption>
        </Tab>
        <Tab className="bg-transparent text-center">
          <InputOption value={CustomerZonePages.PRICE_LIST}>
            Pricing
          </InputOption>
        </Tab>
        <Tab className="bg-transparent text-center">
          <InputOption value={CustomerZonePages.PRODUCT_CATALOGUE}>
            Product catalogue
          </InputOption>
        </Tab>
        {unbundlingEnabled && (
          <Tab className="bg-transparent text-center">
            <InputOption value={CustomerZonePages.DELIVERY_METHOD}>
              Delivery methods
            </InputOption>
          </Tab>
        )}
        {unbundlingRelease2aEnabled && !unbundlingEnabled && (
          <Tab className="bg-transparent text-center">
            <InputOption value={CustomerZonePages.ORDER_RULES}>
              Order rules
            </InputOption>
          </Tab>
        )}
      </InputSelect>
    </TabList>
  );
};

const StorefrontPreviewerButton = ({
  customerZone,
}: {
  customerZone: CustomerZoneGetResponseType;
}) => {
  const {
    openStorefrontPreviewer,
    businessName,
    featureFlags,
    customerZones,
    isSearchingForCustomer,
    isSearchingForCustomerZone,
    customers,
    orderRule,
    selectedCustomer,
    productListQuery,
    tab,
    selectedCustomerZone,
    setSelectedCustomerZone,
    setOpenStorefrontPreviewer,
    setSelectedCustomer,
    setCustomerSearchTerm,
    setCustomerZoneSearchTerm,
    prefillCustomerZone,
    onChangeTab,
    onPreviewStorefront,
  } = useStorefrontPreviewerParameters({
    initialTab: StorefrontPreviewerTabs.CUSTOMER_ZONE,
  });

  return (
    <StorefrontPreviewer
      open={openStorefrontPreviewer}
      initialTab={tab}
      trigger={
        <button
          type="button"
          onClick={() => {
            setOpenStorefrontPreviewer(true);
            prefillCustomerZone({
              customerZone: {
                ...customerZone,
                postcodes: [],
              },
            });
          }}
          color="transparent"
          className="flex w-full cursor-pointer items-center justify-between rounded-none border-t border-t-zinc-200 bg-white p-4 hover:bg-zinc-50"
        >
          <span className="flex items-center gap-2">
            <ListItemIconTile icon="STOREFRONT_OUTLINE" />
            <span className="font-semibold capitalize">Storefront preview</span>
          </span>
          <span>
            <Icon icon="CHEVRON_RIGHT" />
          </span>
        </button>
      }
      businessName={businessName}
      featureFlags={featureFlags}
      customerZoneSearchProps={{
        customerZones,
        isSearchingForCustomerZone,
        selectedCustomerZone,
        onSelectCustomerZone: setSelectedCustomerZone,
        onSearchForCustomerZone: setCustomerZoneSearchTerm,
      }}
      customerSearchProps={{
        customers,
        isSearchingForCustomer,
        selectedCustomer,
        onSelectCustomer: setSelectedCustomer,
        onSearchForCustomer: setCustomerSearchTerm,
      }}
      onTogglePreviewer={setOpenStorefrontPreviewer}
      orderRule={{
        name: orderRule?.name ?? "",
        minimumOrderValue: orderRule?.minimumOrderPrice ?? 0,
        minimumOrderQuantities: orderRule?.PriceTierOrderRules ?? [],
      }}
      productListQuery={productListQuery}
      onClickStorefrontPreview={onPreviewStorefront}
      onChangeTab={onChangeTab}
    />
  );
};
